import { useCallback, useEffect, useState } from "react";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../firebase";

export interface SurveyResponseRow {
  id: string;
  __meta: { submittedAt?: any; createdAt?: any };
  [key: string]: any;
}

export function useSurveyResponses(surveyId?: string, enabled = true, includeDeleted = false) {
  const [responses, setResponses] = useState<SurveyResponseRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!surveyId || !enabled) {
      setResponses([]);
      return;
    }
    setLoading(true);
    try {
      const respSnap = await getDocs(
        query(collection(db, "surveys", surveyId, "responses"), orderBy("submittedAt", "asc"))
      );

      const rows = respSnap.docs.map((docSnap) => {
        const data = docSnap.data();
        if (!includeDeleted && data?.deleted === true) return null;
        const answers = data?.answers ? { ...data.answers } : {};
        Object.keys(answers).forEach((key) => {
          const commentKey = `${key}-Comment`;
          if (
            answers[key] === "other" &&
            typeof answers[commentKey] === "string" &&
            answers[commentKey].trim()
          ) {
            answers[key] = answers[commentKey];
          }
        });
        return {
          id: docSnap.id,
          __meta: { submittedAt: data?.submittedAt, createdAt: data?.createdAt, deleted: data?.deleted },
          ...answers,
        } as SurveyResponseRow;
      }).filter(Boolean) as SurveyResponseRow[];

      setResponses(rows);
      setError(null);
    } catch (e: any) {
      console.error("Failed to fetch responses:", e);
      setError(e?.message || "Failed to load responses.");
    } finally {
      setLoading(false);
    }
  }, [surveyId, enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { responses, loading, error, refresh };
}
